import { useState, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppStore } from '../../store/useAppStore'
import { ENV } from '../../shared/config/env'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import SectionTabs from '../../components/SectionTabs/SectionTabs'
import SearchBar from '../../components/SearchBar/SearchBar'
import CategoryGrid from '../../components/CategoryGrid/CategoryGrid'
import ScrollTopButton from '../HomePage/components/ScrollTopButton'
import ToolsBlock from '../HomePage/components/ToolsBlock'
import logo from '../../assets/images/logo.svg'
import './BasePage.css'

/**
 * «База» — библиотека карточек: разделы, фильтры и поиск.
 *
 * Раньше всё это жило на главной и дублировало её блоки. Логика работы с
 * категориями перенесена сюда без изменений: уровень живёт в адресе, а не в
 * состоянии, иначе кнопка «назад» возвращала страницу на уже пройденный шаг.
 */
const BasePage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { searchQuery, hasActiveSubscription, setSearchQuery } = useAppStore()

  const categoryState = useMemo(
    () => ({
      section: searchParams.get('section') || '',
      subsection: searchParams.get('subsection') || '',
      subSubsection: searchParams.get('subSubsection') || '',
    }),
    [searchParams]
  )

  const [isFirstTabActive, setIsFirstTabActive] = useState(false)

  const hasSubscription = hasActiveSubscription()

  const hasActiveFilters = !!(
    categoryState.section ||
    categoryState.subsection ||
    categoryState.subSubsection
  )

  // Последнее применённое значение: CategoryGrid на один клик зовёт два
  // обработчика подряд, и второй обязан видеть результат первого.
  const appliedRef = useRef(categoryState)
  appliedRef.current = categoryState

  const applyCategories = useCallback(
    (next: { section: string; subsection: string; subSubsection: string }) => {
      const current = appliedRef.current
      if (
        current.section === next.section &&
        current.subsection === next.subsection &&
        current.subSubsection === next.subSubsection
      ) {
        return
      }

      appliedRef.current = next

      const params = new URLSearchParams()
      if (next.section) params.set('section', next.section)
      if (next.subsection) params.set('subsection', next.subsection)
      if (next.subSubsection) params.set('subSubsection', next.subSubsection)

      // В вебе каждый уровень — отдельная запись истории: тогда «назад»
      // поднимает на уровень выше. В Telegram возврат делает системная кнопка
      // через тот же обработчик, и лишние записи сломали бы ей стек.
      setSearchParams(params, { replace: !ENV.IS_WEB })
    },
    [setSearchParams]
  )

  // Шаг на уровень вверх: подподкатегория → подкатегория → категория.
  const handleBackButtonClick = useCallback(() => {
    const current = appliedRef.current
    if (current.subSubsection) {
      applyCategories({ ...current, subSubsection: '' })
    } else if (current.subsection) {
      applyCategories({ ...current, subsection: '', subSubsection: '' })
    } else if (current.section) {
      applyCategories({ section: '', subsection: '', subSubsection: '' })
    }
  }, [applyCategories])

  useTelegramBackButton(true, hasActiveFilters ? handleBackButtonClick : undefined)

  const handleSectionChange = useCallback(
    (section: string) =>
      applyCategories({ section, subsection: '', subSubsection: '' }),
    [applyCategories]
  )

  const handleSubsectionChange = useCallback(
    (subsection: string) =>
      applyCategories({
        ...appliedRef.current,
        subsection,
        subSubsection: '',
      }),
    [applyCategories]
  )

  const handleSubSubsectionChange = useCallback(
    (subSubsection: string) =>
      applyCategories({ ...appliedRef.current, subSubsection }),
    [applyCategories]
  )

  const currentSection = useMemo(
    () =>
      categoryState.subSubsection ||
      categoryState.subsection ||
      categoryState.section,
    [categoryState]
  )

  return (
    <div className="base-page">
      {/* Инструменты — первым экраном, до заголовка библиотеки. */}
      <ToolsBlock />

      <div className="base-page__header">
        <h1 className="base-page__title">Библиотека</h1>
        <img className="base-page__logo" src={logo} alt="Pauline Cake" />
      </div>

      {hasSubscription && (
        <SectionTabs
          activeSection={categoryState.section}
          onSectionChange={handleSectionChange}
          activeSubsection={categoryState.subsection}
          onSubsectionChange={handleSubsectionChange}
          activeSubSubsection={categoryState.subSubsection}
          onSubSubsectionChange={handleSubSubsectionChange}
          onFirstTabStatusChange={setIsFirstTabActive}
        />
      )}

      {hasSubscription && (
        <SearchBar
          placeholder="Поиск по карточкам"
          onSearch={setSearchQuery}
          isFirstTabActive={isFirstTabActive}
        />
      )}

      <CategoryGrid
        section={currentSection}
        searchQuery={searchQuery}
        onSectionChange={handleSectionChange}
        onSubsectionChange={handleSubsectionChange}
        onSubSubsectionChange={handleSubSubsectionChange}
      />

      <ScrollTopButton />
    </div>
  )
}

export default BasePage
